import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { prop } from 'ramda'
import { useSelector } from 'react-redux'
import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { troubleshootingHostsDoc } from 'app/docLinks'
import ResourceUsageIndicator from 'app/plugins/infrastructure/components/common/ResourceUsageIndicator'
import GlanceCertificateStatusIndicator from 'app/plugins/infrastructure/components/common/service-health/GlanceCertificateStatusIndicator'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import Badge from 'core/elements/badge'
import type { BadgeVariant } from 'core/elements/badge/Badge'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { capitalizeString, formatDate } from 'utils/misc'
import { convertToTitleCase } from '../blueprint/helpers'
import type { HostUsage } from '../model'
import {
  blueprintsSelector,
  errorStatuses,
  healthyStatuses,
  pendingStatuses,
  unknownStatuses,
} from '../selectors'
import { getUsageSeverity } from './helpers'
import { IHostDetailsPageTabs } from './host-details/model'
import { VMHABannerState } from './vmha/types'

export const getIconOrBubbleColor = (status, theme: Theme) =>
  ({
    ok: theme.palette.green.main,
    failed: theme.palette.red.main,
  })[status] || theme.palette.grey[500]

export const useStyles = makeStyles<Theme>((theme) => ({
  hostName: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  // baseline keeps the info icon on the label line, not centred against label + bar
  storageRow: { display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) },
  storageAlertWarning: {
    color: theme.palette.graphYellow[500],
  },
  storageAlertDanger: {
    color: theme.palette.graphRed[500],
  },
  storageAlerts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  circle: {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap',
    '&:before': {
      content: '""',
      display: 'inherit',
      height: 12,
      width: 12,
      borderRadius: '50%',
      backgroundColor: getIconOrBubbleColor('ok', theme),
    },
  },
  offline: {
    '&:before': {
      backgroundColor: theme.palette.red.main,
    },
  },
  missingPackages: {
    color: theme.palette.yellow.main,
    marginRight: 6,
  },
  warning: {
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: 12,
    color: getIconOrBubbleColor('ok', theme),
  },
  offlineIcon: {
    color: theme.palette.red.main,
  },
  dot: {
    fontSize: 12,
    marginRight: 4,
  },
  green: {
    color: theme.palette.green.main,
  },
  yellow: {
    color: theme.palette.yellow.main,
  },
  red: {
    color: theme.palette.red.main,
  },
  orange: {
    color: theme.palette.orange.main,
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  ipAddressRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    gap: theme.spacing(1),
  },
  vmhaInProgress: {
    color: theme.palette.red[500],

    '& i': {
      color: theme.palette.red[500],
    },
  },
  vmhaWarning: {
    color: theme.palette.yellow[500],
  },
}))

export const getSearchFunctions = (isVmware) => [
  {
    property: 'roleStatuses',
    searchFn: (value, searchString) => {
      return value.some((role) => {
        return (
          role?.display?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          role?.status?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      })
    },
  },
  {
    property: 'schedulingStatus',
    searchFn: (value, searchString, item) => {
      const underMaintenance = !!item?.hypervisor?.service?.disabled_reason
      return (
        value?.toLowerCase?.().includes(searchString.toLowerCase()) ||
        (underMaintenance && 'Maintenance Mode'.toLowerCase().includes(searchString.toLowerCase()))
      )
    },
  },
  {
    property: 'interfaces',
    searchFn: (value, searchString) => {
      return value.some((iface) => {
        return (
          iface?.name?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          iface?.ip?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      })
    },
  },
]

export const IPAddressCellComponent = ({ value }) => {
  const classes = useStyles()
  return (
    <div>
      {value?.map((iface) => (
        <div key={iface.ip} className={classes.ipAddressRow}>
          <Text variant="body2" component="div">
            {iface.ip?.split('%')?.[0]}
          </Text>
          <Text variant="body2" component="div">
            {iface.name}
          </Text>
        </div>
      ))}
    </div>
  )
}

export const RespondingCell = ({ value, item }) => {
  const classes = useStyles()
  const isOnline = value === 'online'
  return (
    <div>
      <Text variant="body2" className={classes.nowrap}>
        <FontAwesomeIcon
          className={clsx(
            classes.icon,
            { [classes.offlineIcon]: !isOnline, [classes.offline]: !isOnline },
            classes.circle,
          )}
          solid
        >
          circle
        </FontAwesomeIcon>
        {value === 'offline' ? (
          <ExternalLink url={troubleshootingHostsDoc}>{capitalizeString(value)}</ExternalLink>
        ) : (
          capitalizeString(value)
        )}
      </Text>
      {value === 'offline' && item?.lastResponseTime && (
        <Text variant="body2">(since {formatDate(`${item.lastResponseTime}Z`)})</Text>
      )}
    </div>
  )
}

export const StorageAlertBadges = ({
  usage,
}: {
  usage?: Pick<HostUsage, 'disk' | 'ephemeralDisk'>
}) => {
  const classes = useStyles()
  const rootMount = usage?.disk?.mountPoint
  const variants = (['disk', 'ephemeralDisk'] as const).flatMap((key) => {
    const metric = usage?.[key]
    // No mountPoint means legacy disk stats, which never alert.
    if (!metric?.mountPoint || !(metric.max > 0)) return []
    // Same rounding as ResourceUsageIndicator so the badge matches the Storage column.
    const percent = Math.round((metric.current / metric.max) * 100)
    if (percent < 80) return []
    // Ephemeral on the root mount is the same disk; the root check already covers it.
    if (key === 'ephemeralDisk' && metric.mountPoint === rootMount) return []
    return [getUsageSeverity(percent).variant]
  })
  if (!variants.length) return null
  const isDanger = variants.includes('danger')
  // One icon however many partitions are full; the Storage column and host page have details.
  return (
    <div className={classes.storageAlerts}>
      <Tooltip message="Check disk space">
        <span role="img" aria-label="Check disk space">
          <FontAwesomeIcon
            className={isDanger ? classes.storageAlertDanger : classes.storageAlertWarning}
          >
            {isDanger ? 'triangle-exclamation' : 'circle-exclamation'}
          </FontAwesomeIcon>
        </span>
      </Tooltip>
    </div>
  )
}

const VMHA_STATE_LABELS: Partial<Record<VMHABannerState, string>> = {
  [VMHABannerState.Error]: 'Error',
  [VMHABannerState.Warning]: 'Warning',
  [VMHABannerState.Completed]: 'Completed',
}

export const NameWithVMHACell = ({ value, item, vmhaStatuses }: any) => {
  const classes = useStyles()
  const vmhaStatus = vmhaStatuses?.get(item?.id)
  // Check if host has roles
  const hasRoles = item?.v1Roles?.length > 0
  // Show the VMHA banner only when the host is not responding
  const isVMHAEventCompletedWithHostOnline =
    item?.responding === 'online' && vmhaStatus?.state === VMHABannerState.Completed
  const hasVMHA = !!vmhaStatus && !isVMHAEventCompletedWithHostOnline && hasRoles
  const isInProgress = vmhaStatus?.state === VMHABannerState.InProgress

  const getStatusClass = (phase: VMHABannerState): string => {
    switch (phase) {
      case VMHABannerState.InProgress:
      case VMHABannerState.Error:
        return classes.vmhaInProgress
      case VMHABannerState.Warning:
        return classes.vmhaWarning
      case VMHABannerState.Completed:
      default:
        return ''
    }
  }

  const getBadgeVariant = (phase: VMHABannerState): BadgeVariant => {
    switch (phase) {
      case VMHABannerState.Completed:
        return 'default'
      case VMHABannerState.InProgress:
      case VMHABannerState.Error:
        return 'error'
      case VMHABannerState.Warning:
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <div>
      <div className={classes.hostName}>
        <SimpleLink
          src={routeManager.routes.openstack.hostDetails.path({
            id: item?.id,
            tab: IHostDetailsPageTabs.Overview,
          })}
        >
          {value}
        </SimpleLink>
        {hasVMHA && (
          <Badge
            variant={getBadgeVariant(vmhaStatus?.state)}
            text={
              <Text variant="caption2" className={getStatusClass(vmhaStatus?.state)}>
                {isInProgress ? (
                  <>
                    <FontAwesomeIcon size="md" solid spin>
                      sync
                    </FontAwesomeIcon>{' '}
                    VMHA
                  </>
                ) : (
                  `VMHA ${VMHA_STATE_LABELS[vmhaStatus?.state] ?? ''}`
                )}
              </Text>
            }
          />
        )}
      </div>
      <StorageAlertBadges usage={item?.usage} />
    </div>
  )
}

const uberRoleMap = {
  hypervisor: [
    'pf9-neutron-base',
    'pf9-neutron-ovn-controller',
    'pf9-neutron-ovn-metadata-agent',
    'pf9-ostackhost-neutron',
    'pf9-ip-discovery',
  ],
  'persistent-storage': ['pf9-cindervolume-base', 'pf9-cindervolume-config'],
  'image-library': ['pf9-glance-role'],
  dns: ['pf9-designate'],
}

export const WWNCellComponent = ({ value }) => {
  return (
    <div>
      {value?.map((item) => (
        <Tooltip
          key={item.host}
          message={
            <>
              {Object.keys(item).map((key) => (
                <div key={key}>
                  <strong>{convertToTitleCase(key)}:</strong> {item[key]}
                </div>
              ))}
            </>
          }
        >
          {item.host} <FontAwesomeIcon size="sm">info-circle</FontAwesomeIcon>
        </Tooltip>
      ))}
    </div>
  )
}

export const RoleStatusesCell = ({
  value,
  item,
  setUberRoleFailedInfo = null,
  // PCD-9693: surfaces a nearing-expiry/expired Image Library (Glance) certificate warning
  // next to the 'image-library' role row — same per-host indicator as the Service Health
  // tab's Host Health list (ServiceHealthHostsListPage.tsx). Opt-in so the other
  // RoleStatusesCell consumers (host-selection dialogs/modals) are unaffected.
  // See HostsListPage.spec.md FR-008.
  showImageLibraryCertStatus = false,
}) => {
  const classes = useStyles()
  const roles = item?.v1Roles
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  if (!roles?.length) {
    return <Text variant="body2">Unauthorized</Text>
  }
  return (
    <div>
      {value.map((role) => {
        const subRoles = uberRoleMap[role.role]
        const isUberRole = !!subRoles
        return (
          <Text variant="body2" className={classes.nowrap} key={role.role}>
            <FontAwesomeIcon
              className={clsx({
                [classes.dot]: true,
                [classes.green]: healthyStatuses.includes(role.status),
                [classes.orange]: unknownStatuses.includes(role.status),
                [classes.yellow]: pendingStatuses.includes(role.status),
                [classes.red]: errorStatuses.includes(role.status),
              })}
              solid
            >
              circle
            </FontAwesomeIcon>
            {role.display} -{' '}
            {errorStatuses.includes(role.status) ? (
              isUberRole && setUberRoleFailedInfo ? (
                <SimpleLink onClick={() => setUberRoleFailedInfo({ host: item, role, subRoles })}>
                  {role.status}
                </SimpleLink>
              ) : (
                <ExternalLink url={troubleshootingHostsDoc}>{role.status}</ExternalLink>
              )
            ) : (
              role.status
            )}
            {showImageLibraryCertStatus && role.role === 'image-library' && (
              <GlanceCertificateStatusIndicator certInfo={item?.service_cert_info?.glance} />
            )}
          </Text>
        )
      })}
      {isVmware && item?.vmwareRolesIncomplete && (
        <div className={classes.warning}>
          <FontAwesomeIcon className={classes.missingPackages}>
            triangle-exclamation
          </FontAwesomeIcon>
          <Text variant="body2">vSphere Gateway Authorization Incomplete</Text>
        </div>
      )}
    </div>
  )
}

export const ResourceUsageIndicators = ({
  usageStats,
  allocationStats,
  usageValueConverter = null,
  allocationValueConverter = null,
}) => {
  return (
    <>
      <ResourceUsageIndicator
        valueConverter={usageValueConverter?.fn}
        units={usageValueConverter?.units || usageStats?.units}
        label="Used"
        stats={usageStats}
      />
      <ResourceUsageIndicator
        valueConverter={allocationValueConverter?.fn}
        units={allocationValueConverter?.units || allocationStats?.units}
        label="Allocated"
        stats={allocationStats}
      />
    </>
  )
}

// PCD-7719: show Not available when the host reports no filesystem stats instead of a misleading bar
const STORAGE_ROWS = [
  ['Root Disk Used', 'disk'],
  ['Ephemeral Disk Used', 'ephemeralDisk'],
] as const

const InfoHint = ({ message }: { message: string }) => (
  <Tooltip message={message}>
    <span role="img" aria-label={message}>
      <FontAwesomeIcon size="md">info-circle</FontAwesomeIcon>
    </span>
  </Tooltip>
)

export const StorageUsageIndicators = ({
  usage,
}: {
  usage?: Pick<HostUsage, 'disk' | 'ephemeralDisk'>
}) => {
  const classes = useStyles()
  // PCD-4135: hint whether ephemeral (VM instance) storage is shared; hidden until the
  // blueprint is loaded. A PCD region has a single blueprint.
  const blueprint = useSelector(blueprintsSelector)?.[0]
  const sharedMessage =
    blueprint && `Ephemeral storage is ${blueprint.instanceSharedStorage ? 'shared' : 'local'}`

  return (
    <>
      {STORAGE_ROWS.map(([label, key]) => {
        const stats = usage?.[key]
        // one icon per row: mount path plus, on the ephemeral row, the shared/local message
        const message = [
          stats?.path && `Mounted at ${stats.path}`,
          key === 'ephemeralDisk' && sharedMessage,
        ]
          .filter(Boolean)
          .join('. ')
        const hint = message && <InfoHint message={message} />
        return stats ? (
          <div key={label} className={classes.storageRow}>
            <ResourceUsageIndicator units={stats.units} label={label} stats={stats} />
            {hint}
          </div>
        ) : (
          <Text key={label} variant="body2">
            {label}: Not available {hint}
          </Text>
        )
      })}
    </>
  )
}

export const SchedulingStatusCell = (value, item) => {
  if (!item.hypervisor) {
    return 'N/A'
  }
  return (
    <div>
      <Badge
        variant={value === CLUSTER_HOST_SCHEDULING.enabled ? 'success' : 'warning'}
        text={value}
      />
      {item?.hypervisor?.service?.disabled_reason && (
        <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Badge
            variant="warning"
            text={
              item?.hypervisor?.running_vms > 0
                ? 'Maintenance Mode in Progress'
                : 'Maintenance Mode'
            }
            ellipsisAt={30}
            tooltipBody={
              item?.hypervisor?.running_vms > 0
                ? 'Migrations may be in-progress, or may need manual intervention to migrate remaining active VMs. Please check maintenance mode progress within host details.'
                : null
            }
          />
        </div>
      )}
    </div>
  )
}

// FR-015: N/A for non-storage hosts, else Enabled/Partially Enabled/Disabled badge plus a
// Maintenance Mode badge when storage maintenance is on. See HostsListPage.spec.md FR-015.
export const VolumeSchedulingStatusCell = (value, item) => {
  if (!item?.roles?.includes('persistent-storage')) {
    return 'N/A'
  }
  // Some of the host's cinder-volume services enabled and some disabled
  const storageServices = item?.storageServices || []
  const partiallyEnabled =
    storageServices.some((service) => service?.status === 'enabled') &&
    storageServices.some((service) => service?.status === 'disabled')
  const text = partiallyEnabled ? 'Partially Enabled' : value ? 'Enabled' : 'Disabled'
  return (
    <div>
      <Badge variant={value && !partiallyEnabled ? 'success' : 'warning'} text={text} />
      {item?.storageMaintenanceEnabled && (
        <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Badge variant="warning" text={'Maintenance Mode'} ellipsisAt={30} />
        </div>
      )}
    </div>
  )
}
